import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, shallowEqual } from 'react-redux';
import { view } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import topbar from 'src/js/modules/topbar';
import getHighlightedElements from '../selectors/highlighted-elements';
import isNegativeIndent from '../helpers/is-negative-indent';
import calculateHighlighterOffset from '../helpers/calculate-highlighter-offset';
import highlighterOffset from '../helpers/highlighter-offset';
import getHighlighterAppearance from '../helpers/get-highlighter-appearance';
import getHighlighterLabelOptions from '../helpers/get-highlighter-label-options';
import getHighlighterColor from '../helpers/get-highlighter-color';
import getElementBoundsForHighlighter from '../helpers/get-element-bounds-for-highlighter';
import getHighlighterClipPath from '../helpers/get-highlighter-clip-path';

const { Highlighter } = designSystem;

function HighlighterContainer({ viewportHeight = window.innerHeight }) {
  const { scrollTop, elements, isViewLoaded } = useSelector(
    (state) => ({
      scrollTop: view.selectors.getScrollPosition(state),
      elements: getHighlightedElements(state),
      isViewLoaded: view.selectors.getLoadedStatus(state),
    }),
    shallowEqual
  );

  // we need it just to trigger rerender if topbar height was changed
  useSelector(topbar.selectors.getHeight);

  useEffect(() => {
    highlighterOffset.set(calculateHighlighterOffset());
  }, []);

  const [savedViewportHeight, setSavedViewportHeight] = useState(null);

  useEffect(() => {
    // fix for android tablet
    // (on some android tablets vh value is changed after the keyboard open
    // and after keyboard open some extra time is needed to reflow elements due to the changed 100vh value;
    // that's why the highlighter position may be calculated wrong)
    // https://github.com/yola/production/issues/9229
    if (savedViewportHeight !== viewportHeight) {
      requestAnimationFrame(() => {
        setSavedViewportHeight(viewportHeight);
      });
    }
  }, [viewportHeight, savedViewportHeight]);

  const elementsMemo = useMemo(
    () =>
      elements.map(([element, options]) => {
        const computedStyles = getComputedStyle(element);
        return [
          element,
          {
            ...options,
            computedStyles,
          },
        ];
      }),
    [elements]
  );

  if (elements.length === 0 || !isViewLoaded) {
    return null;
  }

  return (
    <React.Fragment>
      {elementsMemo.map(([element, options]) => {
        const elementBounds = getElementBoundsForHighlighter(
          element,
          scrollTop,
          options.computedStyles
        );
        const withNegativeIndent = isNegativeIndent(element);
        const wsId = view.accessors.getLiveElementId(element);
        const appearance = getHighlighterAppearance(element, options);
        const color = getHighlighterColor(element, options);
        const labelProps = getHighlighterLabelOptions(options, elementBounds, element);
        const clipPath = getHighlighterClipPath(element, elementBounds);

        return (
          <Highlighter
            key={wsId}
            color={color}
            elementBounds={elementBounds}
            withNegativeIndent={withNegativeIndent}
            visible={!!element}
            iconGlyph={options.labelIcon}
            appearance={appearance}
            {...(clipPath && { clipPath })}
            {...labelProps}
          />
        );
      })}
    </React.Fragment>
  );
}

HighlighterContainer.propTypes = {
  viewportHeight: PropTypes.number,
};

export default HighlighterContainer;
